import { ReactNode, useState } from 'react'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLock,
  faCheck,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { ButtonV2 } from '@labrav/react-components'
import { useStyles } from './BlockPage.styles'

type Styles = ReturnType<typeof useStyles>

export interface BlockPageProps {
  title: string
  subheading?: string
  features: string[]
  buttonText: string
  onButtonClick?: () => void
  buttonHref?: string
  images?: string[]
  imageAlt?: string
  icon?: ReactNode
}

const PreviewCarousel = ({
  images,
  imageAlt,
  classes,
  cx,
}: {
  images: string[]
  imageAlt: string
  classes: Styles['classes']
  cx: Styles['cx']
}) => {
  const [index, setIndex] = useState(0)
  const count = images.length
  const go = (delta: number) => setIndex(prev => (prev + delta + count) % count)

  return (
    <div className={classes.imagePanel}>
      <div className={classes.carousel}>
        {count > 1 && (
          <button
            type="button"
            aria-label="Previous preview"
            className={cx(classes.arrow, classes.arrowLeft)}
            onClick={() => go(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}

        <img
          src={images[index]}
          alt={imageAlt}
          className={classes.previewImage}
          data-testid="block-page-image"
        />

        {count > 1 && (
          <button
            type="button"
            aria-label="Next preview"
            className={cx(classes.arrow, classes.arrowRight)}
            onClick={() => go(1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>

      {count > 1 && (
        <div className={classes.dots}>
          {images.map((src, i) => (
            <button
              type="button"
              key={src}
              aria-label={`Go to preview ${i + 1}`}
              aria-current={i === index}
              className={i === index ? classes.dotActive : classes.dot}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const BlockPage = ({
  title,
  subheading,
  features,
  buttonText,
  onButtonClick,
  buttonHref,
  images,
  imageAlt = 'Preview',
  icon,
}: BlockPageProps) => {
  const { classes, cx } = useStyles()

  const handleClick =
    onButtonClick ?? (buttonHref ? () => window.open(buttonHref) : undefined)

  return (
    <div className={classes.container} data-testid="block-page">
      <div className={classes.card}>
        {images && images.length > 0 && (
          <PreviewCarousel
            images={images}
            imageAlt={imageAlt}
            classes={classes}
            cx={cx}
          />
        )}
        <div className={classes.content}>
          <div className={classes.titleRow}>
            {icon ?? (
              <FontAwesomeIcon icon={faLock} className={classes.lockIcon} />
            )}
            <Typography className={classes.title}>{title}</Typography>
          </div>

          {subheading && (
            <Typography className={classes.subheading}>{subheading}</Typography>
          )}

          <div className={classes.features}>
            {features.map((feature, i) => (
              <div className={classes.featureRow} key={`${feature}_${i}`}>
                <FontAwesomeIcon icon={faCheck} className={classes.checkIcon} />
                <Typography className={classes.featureText}>
                  {feature}
                </Typography>
              </div>
            ))}
          </div>

          <div className={classes.buttonRow}>
            <ButtonV2
              styleType="primary"
              onClick={handleClick}
              data-testid="block-page-cta"
            >
              {buttonText}
            </ButtonV2>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlockPage
